import { useEffect, useRef, useState } from "react";

/**
 * Parses a CSS color string into [r,g,b,a] in 0–255 / 0–1.
 * Supports rgb(a), hsl(a), hex, and named colors via a temporary canvas.
 * Returns null if the color is fully transparent.
 */
function parseColor(input: string): [number, number, number, number] | null {
  if (!input) return null;
  const s = input.trim();
  if (s === "transparent" || s === "rgba(0, 0, 0, 0)") return null;

  // Fast path: rgb / rgba
  const m = s.match(/^rgba?\(([^)]+)\)$/i);
  if (m) {
    const parts = m[1].split(/[ ,/]+/).filter(Boolean).map(Number);
    const [r, g, b, a = 1] = parts;
    if ([r, g, b].some((n) => Number.isNaN(n))) return null;
    return [r, g, b, a];
  }

  // Fallback via canvas (handles hex, hsl, named).
  if (typeof document === "undefined") return null;
  const ctx = document.createElement("canvas").getContext("2d");
  if (!ctx) return null;
  ctx.fillStyle = "#000";
  ctx.fillStyle = s;
  const computed = ctx.fillStyle as string;
  const m2 = computed.match(/^#([0-9a-f]{6})$/i);
  if (m2) {
    const n = parseInt(m2[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255, 1];
  }
  return null;
}

/** Relative luminance per WCAG. Input r,g,b in 0–255. */
function luminance(r: number, g: number, b: number): number {
  const toLin = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b);
}

/**
 * Walks up the DOM from `el` collecting opaque-enough background colors,
 * blending alpha layers. Returns the effective background luminance, or
 * null if it cannot be determined.
 */
function effectiveBgLuminance(el: HTMLElement): number | null {
  let r = 0, g = 0, b = 0, alphaLeft = 1;
  let node: HTMLElement | null = el;
  while (node && alphaLeft > 0.01) {
    const style = getComputedStyle(node);
    const parsed = parseColor(style.backgroundColor);
    if (parsed) {
      const [pr, pg, pb, pa] = parsed;
      const contribution = pa * alphaLeft;
      r += pr * contribution;
      g += pg * contribution;
      b += pb * contribution;
      alphaLeft *= 1 - pa;
    }
    node = node.parentElement;
  }
  if (alphaLeft > 0.5) {
    // Mostly transparent stack — fall back to <html> color-scheme.
    const root = document.documentElement;
    const dark =
      root.classList.contains("dark") ||
      getComputedStyle(root).colorScheme.includes("dark");
    return dark ? 0.05 : 0.95;
  }
  // Composite over white if there's still residual transparency.
  r += 255 * alphaLeft;
  g += 255 * alphaLeft;
  b += 255 * alphaLeft;
  return luminance(r, g, b);
}

/**
 * Returns "light" or "dark" indicating which logo variant gives the best
 * contrast against the element's actual rendered background.
 *   - "dark"  → use the dark (black) logo (background is light)
 *   - "light" → use the light (white) logo (background is dark)
 *
 * Re-evaluates on theme changes, viewport resize, and DOM class mutations.
 */
export function useContrastVariant<T extends HTMLElement = HTMLElement>(): {
  ref: React.RefObject<T | null>;
  variant: "light" | "dark";
} {
  const ref = useRef<T | null>(null);
  const [variant, setVariant] = useState<"light" | "dark">("dark");

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const compute = () => {
      const lum = effectiveBgLuminance(el);
      if (lum == null) return;
      // Threshold 0.5: lighter background → black logo; darker → white.
      setVariant(lum > 0.5 ? "dark" : "light");
    };

    compute();

    // Re-run on theme toggle (.dark on <html>) and window resize.
    const root = document.documentElement;
    const obs = new MutationObserver(compute);
    obs.observe(root, { attributes: true, attributeFilter: ["class", "data-theme", "style"] });

    let parent: HTMLElement | null = el.parentElement;
    while (parent && parent !== document.body) {
      obs.observe(parent, { attributes: true, attributeFilter: ["class", "style"] });
      parent = parent.parentElement;
    }

    window.addEventListener("resize", compute);
    const id = window.setInterval(compute, 1500); // safety net for animated bgs

    return () => {
      obs.disconnect();
      window.removeEventListener("resize", compute);
      window.clearInterval(id);
    };
  }, []);

  return { ref, variant };
}
